import React from "react";
import { Card, CardProps, Box, Typography, useTheme } from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";

export interface StatCardProps extends Omit<CardProps, "children"> {
  title: string;
  value: string | number;
  trend?: {
    value: number;
    label?: string;
    isPositive?: boolean;
  };
  icon?: React.ReactNode;
  highlighted?: boolean;
}

/**
 * Composant StatCard pour afficher des statistiques (KPIs)
 *
 * @example
 * ```tsx
 * <StatCard
 *   title="Commandes Totales"
 *   value={124}
 *   trend={{ value: 12, label: "vs hier", isPositive: true }}
 *   icon={<ReceiptIcon />}
 * />
 *
 * <StatCard
 *   title="En Attente"
 *   value={15}
 *   highlighted
 *   icon={<HourglassIcon />}
 * />
 * ```
 */
export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  trend,
  icon,
  highlighted = false,
  sx,
  ...props
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        p: 2.5, // p-5
        height: "128px", // h-32
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderLeft: highlighted
          ? `4px solid ${theme.palette.primary.main}`
          : undefined,
        position: "relative",
        overflow: "hidden",
        ...sx,
      }}
      {...props}
    >
      {/* Icône décorative en arrière-plan si highlighted */}
      {highlighted && (
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            p: 2,
            opacity: 0.05,
            pointerEvents: "none",
          }}
        >
          {icon}
        </Box>
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          zIndex: 10,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
        {icon && (
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1,
              bgcolor: highlighted
                ? `${theme.palette.primary.main}1A` // primary/10
                : theme.palette.mode === "light"
                  ? "#eff6ff" // blue-50
                  : "#1e3a5f",
              color: highlighted
                ? theme.palette.primary.main
                : theme.palette.mode === "light"
                  ? "#2563eb" // blue-600
                  : "#60a5fa",
            }}
          >
            {icon}
          </Box>
        )}
      </Box>

      {/* Value and Trend */}
      <Box sx={{ zIndex: 10, minWidth: 0, overflow: "hidden" }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: "30px",
            fontWeight: 700,
            color: highlighted ? theme.palette.primary.main : "text.primary",
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
        </Typography>
        {trend && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            {trend.isPositive !== false ? (
              <TrendingUp sx={{ fontSize: 14, color: "success.main" }} />
            ) : (
              <TrendingDown sx={{ fontSize: 14, color: "error.main" }} />
            )}
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color:
                  trend.isPositive !== false ? "success.main" : "error.main",
              }}
            >
              {trend.value > 0 ? "+" : ""}
              {trend.value}%
              {trend.label && (
                <Typography
                  component="span"
                  sx={{ color: "text.secondary", fontWeight: 400, ml: 0.5 }}
                >
                  {trend.label}
                </Typography>
              )}
            </Typography>
          </Box>
        )}
        {!trend && highlighted && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "12px", mt: 0.5 }}
          >
            Nécessite une attention
          </Typography>
        )}
      </Box>
    </Card>
  );
};
